/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Atom, PrimitiveAtom, WritableAtom, atom } from 'jotai';
import { pitStrategyAtom } from 'jotai/pitStrategy';
import { lapReplayControllerAtom } from 'jotai/raceReplay';
import { activeSanctioningBodyIdAtom } from 'jotai/sanctioningBody';
import { activeEventSelector, activeSessionReplayEnabledSelector, activeSessionSelector } from 'jotai/session';
import { atomFamily, unwrap } from 'jotai/utils';
import { isEqual } from 'lodash';
import {
  OriginType,
  PitRoadData,
  PitStrategyData,
  PitStrategySource,
  SanctioningBodyEnum,
  SessionPitRoadSummary,
} from 'models/viewModels';
import { historicalRaceServiceServer } from 'services/historicalRace/HistoricalRaceService';
import { pitRoadDataService as pitRoadDataServiceRsocket } from 'services/stream/RSocket';
import {
    LapDataLastPit,
    PitDataSummaryInputProps, PitRoadSummaryDataObject,
    VehicleLastPitMap,
} from './PitRoadObject';

let listenerId: string;

const overwrittenPitRoadAtomFamily = atomFamily<OriginType, PrimitiveAtom<PitRoadData[]>>(
  (originType: OriginType) => atom([] as PitRoadData[]),
  (a, b) => isEqual(a, b)
);
const initPitRoadAtomFamily = atomFamily<OriginType, WritableAtom<null, [], () => void>>(
  (dataSource: OriginType) => {
    const newAtom = atom<null, [], () => void>(null, (get, set) => {
      const sanctioningBody = get(activeSanctioningBodyIdAtom) ?? SanctioningBodyEnum.UNKNOWN;
      listenerId = pitRoadDataServiceRsocket
        .get(sanctioningBody)!
        .get(dataSource)!
        .addListener(
          async (pitRoadData) => {
            set(overwrittenPitRoadAtomFamily(dataSource), pitRoadData);
          },
          async () => {
            set(overwrittenPitRoadAtomFamily(dataSource), []);
          }
        );
      return () => {
        pitRoadDataServiceRsocket.get(sanctioningBody)?.get(dataSource)?.removeListener(listenerId);
      };
    });
    newAtom.onMount = (init) => {
      init();
    };
    return newAtom;
  },
  (a, b) => isEqual(a, b)
);

export const pitRoadAtomFamily = atomFamily<OriginType, Atom<PitRoadData[]>>(
  (dataSource: OriginType) =>
    atom<PitRoadData[]>((get) => {
      get(initPitRoadAtomFamily(dataSource));
      return get(overwrittenPitRoadAtomFamily(dataSource));
    }),
  (a, b) => isEqual(a, b)
);

export const pitRoadSummarySelectorFamily = atomFamily<PitDataSummaryInputProps, Atom<PitRoadSummaryDataObject[]>>(
  (props: PitDataSummaryInputProps) =>
    atom<PitRoadSummaryDataObject[]>((get) => {
      const isReplay = get(activeSessionReplayEnabledSelector);
      const pitRoad = isReplay
        ? get(pitRoadSummaryReplayCurrentLapDataSelectorFamily(props.dataSource))
        : get(pitRoadAtomFamily(props.dataSource));
      const event = get(activeEventSelector);
      return pitRoad.map((obj) => {
        const greenIn = props.lineCrossingSelections.greenIn
          ? obj.greenInList.find((item) => item.lineCrossing === props.lineCrossingSelections.greenIn)?.value
          : undefined;
        const greenOut = props.lineCrossingSelections.greenOut
          ? obj.greenOutList.find((item) => item.lineCrossing === props.lineCrossingSelections.greenOut)?.value
          : undefined;
        if (!isReplay) {
          return {
            ...obj,
            ...obj.pitPositionMetadata,
            lap: obj.lap,
            verifiedPitStopType: obj.verifiedPitStopType,
            pitStopType: obj.pitStopType,
            leaderLap: obj.leaderLap,
            source: obj.source ? obj.source : PitStrategySource.UNKNOWN,
            eventName: event?.eventName || '',
            sessionDate: event?.eventDate || '',
            greenIn: greenIn ? (greenIn / 1000).toFixed(3) : undefined,
            greenOut: greenOut ? (greenOut / 1000).toFixed(3) : undefined,
          } as PitRoadSummaryDataObject;
        }
        return {
          ...obj,
          ...obj.pitPositionMetadata,
          eventName: event?.eventName || '',
          sessionDate: event?.eventDate || '',
          greenIn: greenIn ? (greenIn / 1000).toFixed(3) : undefined,
          greenOut: greenOut ? (greenOut / 1000).toFixed(3) : undefined,
        } as PitRoadSummaryDataObject;
      });
    }),
  (a, b) => isEqual(a, b)
);

const pitRoadSummaryReplayDataSelector = atom<Promise<SessionPitRoadSummary[] | undefined>>(async (get) => {
  const eventId = get(activeEventSelector)?.eventId;
  const sessionType = get(activeSessionSelector)?.sessionType;
  const sessionId = get(activeSessionSelector)?.sessionId;
  return eventId && sessionType && sessionId
    ? await historicalRaceServiceServer.getReplaySessionPitRoadSummaryData(
        eventId,
        sessionType,
        sessionId,
        OriginType.ERDP
      )
    : [];
});

const unwrappedPitRoadSummaryReplayDataSelector = unwrap(pitRoadSummaryReplayDataSelector);

const pitRoadSummaryReplayCurrentLapDataSelector = atom<PitRoadData[]>((get) => {
  const currentLap = get(lapReplayControllerAtom);
  const allData = get(unwrappedPitRoadSummaryReplayDataSelector);
  const pitData = allData?.filter((item) => item.leaderLap <= currentLap);
  return pitData ? pitData.flatMap((item) => item.pitRoadData) : [];
});

const pitRoadSummaryReplayDataSelectorFamily = atomFamily<
  OriginType,
  Atom<Promise<SessionPitRoadSummary[] | undefined>>
>((dataSource: OriginType) =>
  atom<Promise<SessionPitRoadSummary[] | undefined>>(async (get) => {
    const eventId = get(activeEventSelector)?.eventId;
    const sessionType = get(activeSessionSelector)?.sessionType;
    const sessionId = get(activeSessionSelector)?.sessionId;
    return eventId && sessionType && sessionId && dataSource
      ? await historicalRaceServiceServer.getReplaySessionPitRoadSummaryData(
          eventId,
          sessionType,
          sessionId,
          dataSource
        )
      : [];
  })
);

const pitRoadSummaryReplayCurrentLapDataSelectorFamily = atomFamily<OriginType, Atom<PitRoadData[]>>(
  (dataSource: OriginType) =>
    atom<PitRoadData[]>((get) => {
      const currentLap = get(lapReplayControllerAtom);
      const allData = get(unwrap(pitRoadSummaryReplayDataSelectorFamily(dataSource)));
      const pitData = allData?.filter((item) => item.leaderLap <= currentLap);
      return pitData ? pitData.flatMap((item) => item.pitRoadData) : [];
    })
);

export const pitRoadDataLastPitSelector = atom<VehicleLastPitMap>((get) => {
  const isReplay = get(activeSessionReplayEnabledSelector);
  let pitRoad;
  if (isReplay) {
    pitRoad = get(pitRoadSummaryReplayCurrentLapDataSelector);
    const record = pitRoad.reduce((acc, current) => {
      const { vehicleNumber, ...pitData } = current;
      if (pitData.lap > 0 && (!acc[vehicleNumber]?.ppLap || pitData.lap > acc[vehicleNumber]?.ppLap)) {
        acc[vehicleNumber] = { ppLap: pitData.lap, ppStrat: pitData.verifiedPitStopType, source: pitData.source, } as LapDataLastPit;
      }
      return acc;
    }, {} as VehicleLastPitMap);
    return record;
  } else {
    pitRoad = get(pitStrategyAtom);
    const vehicleLastPitMap = pitRoad.reduce((prev, current) => {
      const { vehicleNumber, ...pitData } = current;
      if (
        pitData?.lap > 0 &&
        (pitData?.pitStrategySourceMap[PitStrategySource.PITRHO]?.pitStopType ||
          !prev[vehicleNumber]?.ppLap ||
          pitData?.lap > prev[vehicleNumber]?.ppLap)
      ) {
        const lapDataPit = getLapDataLastPitFromStrategy(current);
        if (lapDataPit) {
          prev[vehicleNumber] = {
            ppLap: lapDataPit.ppLap,
            ppStrat: lapDataPit.ppStrat,
            source: lapDataPit.source,
          };
        }
      }
      return prev;
    }, {} as VehicleLastPitMap);
    return vehicleLastPitMap;
  }
});

const getLapDataLastPitFromStrategy = (strategyData: PitStrategyData | undefined): LapDataLastPit | undefined => {
  if (strategyData?.pitStrategySourceMap[PitStrategySource.PITRHO]?.pitStopType) {
    return {
      ppLap: strategyData.lap,
      ppStrat: strategyData.pitStrategySourceMap[PitStrategySource.PITRHO]?.pitStopType,
      ppLeaderLap: strategyData.leaderLap,
      // Currently source for strategy is "UNKNOWN" for all so need to manually set
      source: PitStrategySource.PITRHO,
    } as LapDataLastPit;
  } else if (strategyData?.pitStrategySourceMap[PitStrategySource.SMT]?.pitStopType) {
    return {
      ppLap: strategyData.lap,
      ppStrat: strategyData.pitStrategySourceMap[PitStrategySource.SMT]?.pitStopType,
      ppLeaderLap: strategyData.leaderLap,
      // Currently source for strategy is "UNKNOWN" for all so need to manually set
      source: PitStrategySource.SMT,
    } as LapDataLastPit;
  } else if (strategyData?.pitStrategySourceMap[PitStrategySource.PROPENALTY]?.pitStopType) {
    return {
      ppLap: strategyData.lap,
      ppStrat: strategyData.pitStrategySourceMap[PitStrategySource.PROPENALTY]?.pitStopType,
      ppLeaderLap: strategyData.leaderLap,
      // Currently source for strategy is "UNKNOWN" for all so need to manually set
      source: PitStrategySource.PROPENALTY,
    } as LapDataLastPit;
  }
};
